import React, { useState, useRef, useMemo } from 'react';
import { useData } from '../context/DataContext';
import { Trash2, Edit2, Plus, ArrowRightLeft, Download, Upload, AlertCircle, X, Link, ArrowUp, ArrowDown, Search, SlidersHorizontal, ChevronDown, HelpingHand, Split } from 'lucide-react';
import { ModalAgregarTransaccion } from '../components/ModalAgregarTransaccion';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { Transaccion } from '../types';
import { exportarTransaccionesCSV, parsearCSV } from '../utils/csvUtils';
import { CATEGORIA_ADELANTO, personaDe } from '../utils/transaccionUtils';
import { useConfirmar } from '../context/ConfirmarContext';
import { usePerfil } from '../context/PerfilContext';
import { usePlus } from '../context/PlusContext';
import { LIMITES_USO } from '../config/planes';

type FiltroPeriodo = 'este-mes' | 'mes-anterior' | 'rango' | 'todos';
/** Distintivo de las transacciones que vienen de un plan de A medias. */
const EtiquetaAMedias: React.FC = () => (
  <span title="Viene de un plan de A medias: importe, fecha y descripción se actualizan desde el plan"
    className="inline-flex items-center gap-0.5 mr-1.5 align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded-full
               bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300">
    <Split className="w-3 h-3" /> A medias
  </span>
);

type FiltroTipo    = 'todos' | 'ingresos' | 'gastos' | 'traspasos' | 'adelantos';
type Direccion     = 'desc' | 'asc';

type FilaTabla =
  | { kind: 'transaccion'; data: Transaccion; seq: number }
  | { kind: 'traspaso';    data: { id: string; cuentaOrigenId: string; cuentaDestinoId?: string; inversionDestinoId?: string; cantidad: number; descripcion: string; fecha: Date; secuencia?: number }; seq: number };

export const Transacciones: React.FC = () => {
  const confirmar = useConfirmar();
  const { esPlus } = usePerfil();
  const { ofrecerPlus } = usePlus();
  const { transacciones, traspasos, eliminarTransaccion, eliminarTraspaso, crearTransaccion, cuentas, categorias, loading } = useData();

  const [showModal, setShowModal]             = useState(false);
  const [transaccionEditar, setTransaccionEditar] = useState<Transaccion | undefined>(undefined);

  // ── Filtros ───────────────────────────────────────────────────────────────
  const [filtroTipo, setFiltroTipo]           = useState<FiltroTipo>('todos');
  const [filtroPeriodo, setFiltroPeriodo]     = useState<FiltroPeriodo>('este-mes');
  const [rangoDesde, setRangoDesde]           = useState('');
  const [rangoHasta, setRangoHasta]           = useState('');
  const [filtroCuenta, setFiltroCuenta]       = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [busqueda, setBusqueda]               = useState('');
  const [direccion, setDireccion]             = useState<Direccion>('desc');

  // ── CSV ───────────────────────────────────────────────────────────────────
  const [importando, setImportando]           = useState(false);
  const [resultadoImport, setResultadoImport] = useState<{ ok: number; errores: string[] } | null>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);

  const getNombreCuenta = (id: string) => cuentas.find(c => c.id === id)?.nombre || 'Desconocida';
  const getCategoria    = (id: string) => categorias.find(c => c.id === id);

  // ── Rango de fechas ───────────────────────────────────────────────────────
  const rangoActivo = useMemo<[Date, Date] | null>(() => {
    const hoy = new Date();
    if (filtroPeriodo === 'este-mes')    return [startOfMonth(hoy), endOfMonth(hoy)];
    if (filtroPeriodo === 'mes-anterior') {
      const ant = subMonths(hoy, 1);
      return [startOfMonth(ant), endOfMonth(ant)];
    }
    if (filtroPeriodo === 'rango' && rangoDesde && rangoHasta) {
      const d = new Date(rangoDesde); d.setHours(0, 0, 0, 0);
      const h = new Date(rangoHasta); h.setHours(23, 59, 59, 999);
      return [d, h];
    }
    return null;
  }, [filtroPeriodo, rangoDesde, rangoHasta]);

  const enRango = (fecha: Date) => {
    if (!rangoActivo) return true;
    const f = new Date(fecha).getTime();
    return f >= rangoActivo[0].getTime() && f <= rangoActivo[1].getTime();
  };

  // ── Lista unificada ───────────────────────────────────────────────────────
  const filas: FilaTabla[] = useMemo(() => {
    const term = busqueda.toLowerCase().trim();

    const txFiltradas = transacciones.filter(t => {
      if (!enRango(t.fecha)) return false;
      if (filtroTipo === 'ingresos'  && t.tipo !== 'ingreso') return false;
      if (filtroTipo === 'gastos'    && t.tipo !== 'gasto')   return false;
      if (filtroTipo === 'traspasos') return false;
      if (filtroTipo === 'adelantos' && !t.adelanto) return false;
      // Filtro por cuenta
      if (filtroCuenta && t.cuentaId !== filtroCuenta) return false;
      // Filtro por categoría: excluir también entradas/salidas vinculadas (no tienen categoría real)
      if (filtroCategoria) {
        if (t.traspasoId) return false;
        if (t.categoriaId !== filtroCategoria) return false;
      }
      // Búsqueda libre (descripción, nombre categoría, nombre cuenta)
      if (term) {
        const cat = getCategoria(t.categoriaId);
        const cta = cuentas.find(c => c.id === t.cuentaId);
        const hayMatch =
          (t.descripcion || '').toLowerCase().includes(term) ||
          (cat?.nombre || '').toLowerCase().includes(term) ||
          (cta?.nombre || '').toLowerCase().includes(term) ||
          (t.persona || '').toLowerCase().includes(term) ||
          t.cantidad.toFixed(2).includes(term);
        if (!hayMatch) return false;
      }
      return true;
    });

    const mastersFiltrados =
      filtroTipo === 'ingresos' || filtroTipo === 'gastos' || filtroTipo === 'adelantos'
        ? []
        : traspasos.filter(t => {
            if (!enRango(t.fecha)) return false;
            if (filtroCategoria) return false; // traspasos no tienen categoría
            if (filtroCuenta && t.cuentaOrigenId !== filtroCuenta && t.cuentaDestinoId !== filtroCuenta) return false;
            if (term) {
              const hayMatch =
                (t.descripcion || '').toLowerCase().includes(term) ||
                getNombreCuenta(t.cuentaOrigenId).toLowerCase().includes(term) ||
                getNombreCuenta(t.cuentaDestinoId ?? '').toLowerCase().includes(term) ||
                t.cantidad.toFixed(2).includes(term);
              if (!hayMatch) return false;
            }
            return true;
          });

    const lista: FilaTabla[] = [
      ...txFiltradas.map(t => ({ kind: 'transaccion' as const, data: t, seq: t.secuencia ?? 0 })),
      ...mastersFiltrados.map(t => ({ kind: 'traspaso' as const, data: t, seq: t.secuencia ?? 0 })),
    ];

    // Ordenar: primero por fecha, luego por secuencia como desempate
    lista.sort((a, b) => {
      const fa = new Date(a.data.fecha).getTime();
      const fb = new Date(b.data.fecha).getTime();
      if (fa !== fb) return direccion === 'desc' ? fb - fa : fa - fb;
      return direccion === 'desc' ? b.seq - a.seq : a.seq - b.seq;
    });

    return lista;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transacciones, traspasos, filtroTipo, filtroPeriodo, rangoDesde, rangoHasta,
      filtroCuenta, filtroCategoria, busqueda, direccion]);

  const hayFiltrosActivos = filtroCuenta || filtroCategoria || busqueda;
  const limpiarFiltros = () => { setFiltroCuenta(''); setFiltroCategoria(''); setBusqueda(''); };

  // ── Panel de filtros colapsable ───────────────────────────────────────────
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);

  // Cuenta cuántos filtros difieren de sus valores por defecto
  const LABEL_TIPO: Record<FiltroTipo, string> = {
    todos: 'Todas', ingresos: 'Ingresos', gastos: 'Gastos', traspasos: 'Traspasos', adelantos: 'Adelantos',
  };
  const LABEL_PERIODO: Record<FiltroPeriodo, string> = {
    'este-mes': 'Este mes', 'mes-anterior': 'Mes anterior', rango: 'Rango', todos: 'Todos',
  };

  const chipsActivos = [
    filtroTipo     !== 'todos'      && { key: 'tipo',     label: LABEL_TIPO[filtroTipo] },
    filtroPeriodo  !== 'este-mes'   && { key: 'periodo',  label: LABEL_PERIODO[filtroPeriodo] },
    filtroCuenta                    && { key: 'cuenta',   label: cuentas.find(c => c.id === filtroCuenta)?.nombre ?? 'Cuenta' },
    filtroCategoria                 && { key: 'cat',      label: categorias.find(c => c.id === filtroCategoria)?.nombre ?? 'Categoría' },
    busqueda                        && { key: 'busqueda', label: `"${busqueda}"` },
    direccion      !== 'desc'       && { key: 'orden',    label: 'Más antiguo' },
  ].filter(Boolean) as { key: string; label: string }[];

  // ── Acciones ──────────────────────────────────────────────────────────────
  const handleEliminarTransaccion = async (id: string) => {
    // Las de A medias se regeneran mientras el plan siga sincronizado: avisar
    const deUnPlan = transacciones.find(t => t.id === id)?.planId;
    const aviso = deUnPlan
      ? 'Viene de un plan de A medias: si el plan se sigue pasando a Finanzas, ' +
        'volverá a aparecer. Para quitarla de verdad, cambia o borra el gasto en el plan.'
      : undefined;
    if (await confirmar({ titulo: '¿Eliminar esta transacción?', mensaje: aviso, accion: 'Eliminar' }))
      try { await eliminarTransaccion(id); } catch (e) { console.error(e); }
  };
  const handleEliminarTraspaso = async (id: string) => {
    if (await confirmar({
      titulo: '¿Eliminar este traspaso?',
      mensaje: 'Se eliminarán los movimientos vinculados y se revertirán los saldos.',
      accion: 'Eliminar',
    }))
      try { await eliminarTraspaso(id); } catch (e) { console.error(e); }
  };
  const handleEditar  = (t: Transaccion) => { setTransaccionEditar(t); setShowModal(true); };
  const handleCerrar  = () => { setShowModal(false); setTransaccionEditar(undefined); };
  const handleExportCSV = () => exportarTransaccionesCSV(transacciones, categorias, cuentas);

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    setImportando(true); setResultadoImport(null);
    const texto = await archivo.text();
    const { filas: filas2, errores } = parsearCSV(texto);
    // Plan gratis: el archivo entero o nada, para que no quede a medias y al
    // repetirlo no se dupliquen las primeras filas
    if (!esPlus && filas2.length > LIMITES_USO.filasImportacion) {
      setResultadoImport({ ok: 0, errores: [
        `El archivo tiene ${filas2.length} filas y con el plan gratis se importan hasta ${LIMITES_USO.filasImportacion} cada vez. Divídelo en varios archivos o hazte Plus.`,
      ] });
      setImportando(false);
      if (inputFileRef.current) inputFileRef.current.value = '';
      ofrecerPlus('importar');
      return;
    }
    let ok = 0;
    for (const f of filas2) {
      const cta = cuentas.find(c => c.nombre.toLowerCase() === f.cuentaNombre.toLowerCase());
      // Los adelantos se exportan con la categoría 'Adelanto'
      const esAdelanto = f.categoriaNombre.toLowerCase() === 'adelanto';
      const cat = esAdelanto ? undefined : categorias.find(c => c.nombre.toLowerCase() === f.categoriaNombre.toLowerCase() && c.tipo === f.tipo);
      if (!esAdelanto && !cat) { errores.push(`Categoría '${f.categoriaNombre}' no encontrada`); continue; }
      if (!cta) { errores.push(`Cuenta '${f.cuentaNombre}' no encontrada`); continue; }
      try {
        await crearTransaccion({
          tipo: f.tipo, cantidad: f.cantidad, cuentaId: cta.id, descripcion: f.descripcion, fecha: new Date(f.fecha),
          ...(esAdelanto
            ? { categoriaId: CATEGORIA_ADELANTO, adelanto: true, ...(f.persona ? { persona: f.persona } : {}) }
            : { categoriaId: cat!.id }),
        });
        ok++;
      } catch (err: any) { errores.push(`Error (${f.fecha}): ${err.message}`); }
    }
    setResultadoImport({ ok, errores });
    setImportando(false);
    if (inputFileRef.current) inputFileRef.current.value = '';
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-gray-600 dark:text-gray-400">Cargando…</div>
    </div>
  );

  const selectClass = "px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">

        {/* Cabecera */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Transacciones</h1>
          <div className="flex flex-wrap gap-2">
            <button onClick={handleExportCSV} title="Exportar CSV"
              className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold py-2 px-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition text-sm">
              <Download className="w-4 h-4" /> CSV
            </button>
            <label title="Importar CSV"
              className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold py-2 px-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition text-sm cursor-pointer">
              <Upload className="w-4 h-4" />
              {importando ? 'Importando…' : 'Importar CSV'}
              <input ref={inputFileRef} type="file" accept=".csv" className="hidden" onChange={handleImportCSV} disabled={importando} />
            </label>
            <button onClick={() => { setTransaccionEditar(undefined); setShowModal(true); }}
              className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition">
              <Plus className="w-5 h-5" /> Nueva
            </button>
          </div>
        </div>

        {/* Resultado importación */}
        {resultadoImport && (
          <div className={`rounded-lg p-4 mb-4 flex items-start gap-3 ${resultadoImport.errores.length === 0
            ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700'
            : 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700'}`}>
            <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${resultadoImport.errores.length === 0 ? 'text-green-600' : 'text-yellow-600'}`} />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-gray-900 dark:text-white">{resultadoImport.ok} importada(s)</p>
              {resultadoImport.errores.length > 0 && (
                <ul className="mt-1 text-yellow-800 dark:text-yellow-300 list-disc list-inside space-y-0.5">
                  {resultadoImport.errores.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
            <button onClick={() => setResultadoImport(null)}><X className="w-4 h-4 text-gray-400" /></button>
          </div>
        )}

        {/* ── Barra de filtros colapsable ──────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow mb-4">

          {/* Cabecera — siempre visible */}
          <button
            type="button"
            onClick={() => setFiltrosAbiertos(v => !v)}
            className="w-full flex items-center gap-2 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition rounded-xl"
          >
            <SlidersHorizontal className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Filtros</span>

            {/* Badge con número de filtros activos */}
            {chipsActivos.length > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold shrink-0">
                {chipsActivos.length}
              </span>
            )}

            {/* Chips resumen — visibles solo cuando el panel está cerrado */}
            {!filtrosAbiertos && chipsActivos.length > 0 && (
              <div className="flex flex-wrap gap-1 ml-1">
                {chipsActivos.map(c => (
                  <span key={c.key}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
                    {c.label}
                  </span>
                ))}
              </div>
            )}

            {/* Botón limpiar — accesible aunque el panel esté cerrado */}
            {chipsActivos.length > 0 && (
              <button
                type="button"
                onClick={e => { e.stopPropagation(); limpiarFiltros(); setFiltroTipo('todos'); setFiltroPeriodo('este-mes'); setDireccion('desc'); }}
                className="ml-auto mr-1 flex items-center gap-1 text-xs text-red-500 dark:text-red-400 hover:text-red-700 font-semibold shrink-0"
              >
                <X className="w-3 h-3" /> Limpiar
              </button>
            )}

            <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${filtrosAbiertos ? 'rotate-180' : ''} ${chipsActivos.length > 0 ? '' : 'ml-auto'}`} />
          </button>

          {/* Cuerpo colapsable */}
          {filtrosAbiertos && (
            <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-gray-700 pt-3">

              {/* Fila 1: Tipo + orden */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-14 shrink-0">Tipo</span>
                {(['todos', 'ingresos', 'gastos', 'traspasos', 'adelantos'] as FiltroTipo[]).map(f => (
                  <button key={f} onClick={() => setFiltroTipo(f)}
                    className={`px-3 py-1.5 rounded-lg font-semibold transition text-sm ${
                      filtroTipo === f
                        ? f === 'ingresos' ? 'bg-green-600 text-white'
                          : f === 'gastos' ? 'bg-red-600 text-white'
                          : f === 'traspasos' ? 'bg-purple-600 text-white'
                          : f === 'adelantos' ? 'bg-amber-600 text-white'
                          : 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}>
                    {LABEL_TIPO[f]}
                  </button>
                ))}
                <button onClick={() => setDireccion(d => d === 'desc' ? 'asc' : 'desc')}
                  className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition text-sm font-semibold">
                  {direccion === 'desc'
                    ? <><ArrowDown className="w-4 h-4" /> Reciente</>
                    : <><ArrowUp   className="w-4 h-4" /> Antiguo</>}
                </button>
              </div>

              {/* Fila 2: Período */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-14 shrink-0">Fecha</span>
                {([
                  { v: 'este-mes',     l: 'Este mes'    },
                  { v: 'mes-anterior', l: 'Mes anterior'},
                  { v: 'rango',        l: 'Rango'       },
                  { v: 'todos',        l: 'Todos'        },
                ] as { v: FiltroPeriodo; l: string }[]).map(({ v, l }) => (
                  <button key={v} onClick={() => setFiltroPeriodo(v)}
                    className={`px-3 py-1.5 rounded-lg font-semibold transition text-sm ${
                      filtroPeriodo === v
                        ? 'bg-gray-700 dark:bg-gray-200 text-white dark:text-gray-900'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}>
                    {l}
                  </button>
                ))}
                {filtroPeriodo === 'rango' && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <input type="date" value={rangoDesde} onChange={e => setRangoDesde(e.target.value)} className={selectClass} />
                    <span className="text-gray-400 text-sm">—</span>
                    <input type="date" value={rangoHasta} onChange={e => setRangoHasta(e.target.value)} className={selectClass} />
                  </div>
                )}
              </div>

              {/* Fila 3: Cuenta, categoría, búsqueda */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-14 shrink-0">Filtrar</span>
                <select value={filtroCuenta} onChange={e => setFiltroCuenta(e.target.value)} className={selectClass}>
                  <option value="">Todas las cuentas</option>
                  {cuentas.map(c => <option key={c.id} value={c.id}>{c.icono ? `${c.icono} ` : ''}{c.nombre}</option>)}
                </select>
                <select value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} className={selectClass}>
                  <option value="">Todas las categorías</option>
                  {categorias.map(c => <option key={c.id} value={c.id}>{c.icono} {c.nombre}</option>)}
                </select>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" />
                  <input type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)}
                    placeholder="Descripción, persona, importe…"
                    className={`${selectClass} pl-8 min-w-44`} />
                </div>
              </div>
            </div>
          )}
        </div>

        {filas.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-8 text-center text-gray-500 dark:text-gray-400">
            No hay registros para los filtros seleccionados
          </div>
        ) : (
          <>
            {/* ── Vista tarjetas — móvil ─────────────────────────────────── */}
            <div className="md:hidden space-y-2">
              {filas.map((fila, idx) => {
                const numeroFila = direccion === 'desc' ? filas.length - idx : idx + 1;

                if (fila.kind === 'traspaso') {
                  const t = fila.data;
                  return (
                    <div key={`traspaso-card-${t.id}`}
                      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border-l-4 border-purple-500 px-4 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="text-xs font-bold bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                              <ArrowRightLeft className="w-2.5 h-2.5" /> Traspaso
                            </span>
                            <span className="text-xs text-gray-400">#{numeroFila}</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                            {getNombreCuenta(t.cuentaOrigenId)} → {t.inversionDestinoId ? '📈 Inversión' : getNombreCuenta(t.cuentaDestinoId ?? '')}
                          </p>
                          {t.descripcion && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{t.descripcion}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-0.5">
                            {format(new Date(t.fecha), 'dd MMM yyyy', { locale: es })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-base font-bold text-purple-600 dark:text-purple-400">
                            €{t.cantidad.toFixed(2)}
                          </span>
                          <button onClick={() => handleEliminarTraspaso(t.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }

                const t = fila.data;
                const esVinculada = !!t.traspasoId;
                const esAdelanto = !!t.adelanto;
                const cat = getCategoria(t.categoriaId);
                const esIngreso = t.tipo === 'ingreso';

                return (
                  <div key={`tx-card-${t.id}`}
                    className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border-l-4 px-4 py-3 ${
                      esVinculada
                        ? esIngreso ? 'border-blue-400' : 'border-orange-400'
                        : esAdelanto ? 'border-amber-400'
                        : esIngreso ? 'border-green-500' : 'border-red-500'
                    }`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        {/* Categoría + fecha */}
                        <div className="flex items-center gap-2 mb-0.5">
                          {esAdelanto ? (
                            <HelpingHand className="w-4 h-4 text-amber-600" />
                          ) : !esVinculada && cat ? (
                            <span className="text-sm">{cat.icono}</span>
                          ) : null}
                          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                            esVinculada
                              ? esIngreso
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                                : 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300'
                              : esAdelanto
                                ? 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300'
                              : esIngreso
                                ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                                : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
                          }`}>
                            {esVinculada
                              ? (esIngreso ? 'Entrada' : 'Salida')
                              : esAdelanto
                                ? (esIngreso ? 'Devolución' : 'Adelanto')
                              : (esIngreso ? 'Ingreso' : 'Gasto')}
                          </span>
                          <span className="text-xs text-gray-400 truncate">
                            {esVinculada
                              ? 'Traspaso'
                              : esAdelanto
                                ? `${personaDe(t)}${t.saldado ? ' · saldado' : ''}`
                              : cat?.nombre ?? 'Sin cat.'}
                          </span>
                        </div>
                        {/* Descripción */}
                        {t.descripcion ? (
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                            {t.planId && <EtiquetaAMedias />}
                            {t.descripcion}
                          </p>
                        ) : null}
                        {/* Cuenta + fecha */}
                        <p className="text-xs text-gray-400 mt-0.5">
                          {getNombreCuenta(t.cuentaId)} · {format(new Date(t.fecha), 'dd MMM yyyy', { locale: es })}
                        </p>
                      </div>

                      {/* Importe + acciones */}
                      <div className="flex items-center gap-1 shrink-0">
                        <span className={`text-base font-bold ${esIngreso ? 'text-green-600' : 'text-red-600'}`}>
                          {esIngreso ? '+' : '-'}€{t.cantidad.toFixed(2)}
                        </span>
                        {!esVinculada && (
                          <>
                            <button onClick={() => handleEditar(t)}
                              className="p-1.5 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleEliminarTransaccion(t.id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Tabla — desktop ────────────────────────────────────────── */}
            <div className="hidden md:block bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-10">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Categoría</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Cuenta</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Descripción</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Importe</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filas.map((fila, idx) => {
                      const numeroFila = direccion === 'desc' ? filas.length - idx : idx + 1;

                      if (fila.kind === 'traspaso') {
                        const t = fila.data;
                        return (
                          <tr key={`traspaso-${t.id}`} className="bg-purple-50/40 dark:bg-purple-900/10 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition">
                            <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500">{numeroFila}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{format(new Date(t.fecha), 'dd MMM yyyy', { locale: es })}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full">
                                <ArrowRightLeft className="w-3 h-3" /> Traspaso
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-400 italic">—</td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 font-medium">
                              {getNombreCuenta(t.cuentaOrigenId)}<span className="mx-1 text-gray-400">→</span>{t.inversionDestinoId ? '📈 Inversión' : getNombreCuenta(t.cuentaDestinoId ?? '')}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{t.descripcion || '-'}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-right text-purple-600 dark:text-purple-400">€{t.cantidad.toFixed(2)}</td>
                            <td className="px-4 py-3 text-center">
                              <button onClick={() => handleEliminarTraspaso(t.id)} className="text-red-600 hover:text-red-800 transition">
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        );
                      }

                      const t = fila.data;
                      const esVinculada = !!t.traspasoId;
                      const esAdelanto = !!t.adelanto;
                      const cat = getCategoria(t.categoriaId);
                      return (
                        <tr key={`tx-${t.id}`} className={`transition ${esVinculada ? 'bg-purple-50/20 dark:bg-purple-900/5 hover:bg-purple-50/40 dark:hover:bg-purple-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                          <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500">{numeroFila}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{format(new Date(t.fecha), 'dd MMM yyyy', { locale: es })}</td>
                          <td className="px-4 py-3">
                            {esVinculada ? (
                              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${t.tipo === 'ingreso' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' : 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300'}`}>
                                <Link className="w-3 h-3" />{t.tipo === 'ingreso' ? 'Entrada' : 'Salida'}
                              </span>
                            ) : esAdelanto ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300">
                                <HelpingHand className="w-3 h-3" />{t.tipo === 'ingreso' ? 'Devolución' : 'Adelanto'}
                              </span>
                            ) : (
                              <span className={`inline-block text-xs font-semibold px-2 py-1 rounded-full ${t.tipo === 'ingreso' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'}`}>
                                {t.tipo === 'ingreso' ? 'Ingreso' : 'Gasto'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {esVinculada ? <span className="text-gray-400 italic">Traspaso</span> : esAdelanto ? <span className="text-amber-700 dark:text-amber-300">{personaDe(t)}{t.saldado && <span className="text-xs text-gray-400"> · saldado</span>}</span> : cat ? `${cat.icono} ${cat.nombre}` : 'Desconocida'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{getNombreCuenta(t.cuentaId)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {t.planId && <EtiquetaAMedias />}{t.descripcion || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-right">
                            <span className={t.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}>
                              {t.tipo === 'ingreso' ? '+' : '-'}€{t.cantidad.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {esVinculada ? (
                              <span className="text-xs text-gray-400 italic">vinculado</span>
                            ) : (
                              <div className="flex items-center justify-center gap-2">
                                <button onClick={() => handleEditar(t)} className="text-blue-600 hover:text-blue-800 transition"><Edit2 className="w-5 h-5" /></button>
                                <button onClick={() => handleEliminarTransaccion(t.id)} className="text-red-600 hover:text-red-800 transition"><Trash2 className="w-5 h-5" /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Contador de resultados */}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-right">
          {filas.length} registro(s)
        </p>
      </div>

      {showModal && <ModalAgregarTransaccion onClose={handleCerrar} transaccionEditar={transaccionEditar} />}
    </div>
  );
};
